import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const shiftDate = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const AdminScheduler = () => {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/venues", { withCredentials: true });
        setVenues(data);
        if (data.length) setVenueId(data[0].id);
      } catch {
        toast.error("Could not load venues.");
      }
    })();
  }, []);

  const reload = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/scheduler?venue_id=${venueId}&date=${date}`, { withCredentials: true });
      setGrid(data);
    } catch {
      toast.error("Could not load scheduler.");
    } finally {
      setLoading(false);
    }
  }, [venueId, date]);

  useEffect(() => { reload(); }, [reload]);

  const colorFor = (cell) => {
    if (cell.is_excluded) return "bg-slate-100 border-slate-200 text-slate-400";
    if (!cell.booking) return "bg-emerald-50 border-emerald-100 hover:border-emerald-300 text-emerald-700";
    if (cell.booking.booking_unit === "box") return "bg-violet-50 border-violet-200 text-violet-800";
    if (cell.booking.booking_unit === "bulk") return "bg-blue-50 border-blue-200 text-blue-800";
    return "bg-amber-50 border-amber-200 text-amber-800";
  };

  return (
    <div className="space-y-5" data-testid="admin-scheduler">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-spike-blue" /> Lane Booking Scheduler
          </h2>
          <p className="text-sm text-slate-600 mt-1">Day × lanes operational view for any venue.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="sch-venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)} className="bg-white border border-slate-300 rounded-md px-3 py-2 text-sm">
            {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
          </select>
          <div className="flex items-center bg-white border border-slate-300 rounded-md overflow-hidden">
            <button type="button" data-testid="sch-prev-day" onClick={() => setDate((d) => shiftDate(d, -1))} className="px-2 py-2 hover:bg-slate-50">
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <input type="date" data-testid="sch-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="px-2 py-2 text-sm border-x border-slate-300" />
            <button type="button" data-testid="sch-next-day" onClick={() => setDate((d) => shiftDate(d, +1))} className="px-2 py-2 hover:bg-slate-50">
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>
      </div>

      {loading || !grid ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-card overflow-x-auto" data-testid="sch-grid">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-medium text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[80px]">Slot</th>
                {Array.from({ length: grid.lanes_total }, (_, i) => i + 1).map((n) => (
                  <th key={n} className="text-center px-3 py-3 font-medium text-slate-600 min-w-[110px]">Lane {n}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr key={row.time_slot} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-900 sticky left-0 bg-white">{row.time_slot}</td>
                  {row.lanes.map((cell) => (
                    <td key={cell.lane_number} className="px-2 py-1.5">
                      <div
                        data-testid={`sch-cell-${row.time_slot.replace(":", "")}-${cell.lane_number}`}
                        className={`rounded border px-2 py-2 text-xs min-h-[52px] ${colorFor(cell)}`}
                      >
                        {cell.is_excluded ? (
                          <div className="font-medium">— maintenance —</div>
                        ) : cell.booking ? (
                          <>
                            <div className="font-semibold truncate" title={cell.booking.booker_name}>{cell.booking.booker_name}</div>
                            <div className="text-[0.65rem] opacity-80 uppercase tracking-wider mt-0.5">{cell.booking.booking_unit}</div>
                          </>
                        ) : (
                          <div className="font-medium text-center">free</div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-50 border border-emerald-300 rounded" /> Free</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-50 border border-amber-300 rounded" /> Lane booking</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-violet-50 border border-violet-300 rounded" /> Box booking</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-50 border border-blue-300 rounded" /> Bulk booking</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-slate-100 border border-slate-300 rounded" /> Maintenance</span>
      </div>
    </div>
  );
};

export default AdminScheduler;
